/**
 * Purchase Orders Page - Generate and manage purchase orders
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { toMinor, toMajor } from '../../lib/currency';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Modal } from '../../components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  HelpGuide, 
  HelpAlert, 
  HelpTooltip,
  HelpSteps 
} from '../../components/ui/HelpGuide';
import { 
  ShoppingCart, 
  FileDown, 
  FileSpreadsheet,
  Package,
  AlertTriangle,
  Plus,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Edit,
  Eye,
  Bell
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  sent: 'bg-blue-500',
  received: 'bg-green-500',
  cancelled: 'bg-red-500'
};

const STATUS_LABELS = {
  draft: 'Rascunho',
  sent: 'Enviado',
  received: 'Recebido',
  cancelled: 'Cancelado'
};

export default function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [summary, setSummary] = useState({ total_products: 0, total_estimated_cost: 0, suppliers_count: 0 });
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [onlyLowStock, setOnlyLowStock] = useState(true);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState('suggestions'); // suggestions, orders
  const [statusFilter, setStatusFilter] = useState('all'); // all, draft, sent, received, cancelled
  
  // New states for expand, edit and notifications
  const [expandedOrders, setExpandedOrders] = useState({});
  const [editingOrder, setEditingOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterSupplier, onlyLowStock]);

  // Calculate order stats by status
  const orderStats = useMemo(() => {
    const stats = {
      draft: { count: 0, total: 0 },
      sent: { count: 0, total: 0 },
      received: { count: 0, total: 0 },
      cancelled: { count: 0, total: 0 }
    };
    
    orders.forEach(order => {
      const status = order.status || 'draft';
      if (stats[status]) {
        stats[status].count += 1;
        stats[status].total += order.total_cost || 0;
      }
    });
    
    return stats;
  }, [orders]);

  // Filter orders by status
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter(o => o.status === statusFilter);
  }, [orders, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load suppliers
      const suppliersRes = await api.get('/suppliers');
      setSuppliers(suppliersRes.data || []);
      
      // Load suggestions
      const params = new URLSearchParams();
      if (filterSupplier && filterSupplier !== 'all') {
        params.append('supplier_id', filterSupplier);
      }
      params.append('only_low_stock', onlyLowStock);
      
      const suggestionsRes = await api.get(`/purchase-orders/suggestions?${params}`);
      if (suggestionsRes.data?.success) {
        setSuggestions(suggestionsRes.data.data.suggestions || []);
        setBySupplier(suggestionsRes.data.data.by_supplier || []);
        setSummary(suggestionsRes.data.data.summary || {});
      }
      
      // Load existing orders
      const ordersRes = await api.get('/purchase-orders');
      if (ordersRes.data?.success && ordersRes.data?.data?.items) {
        setOrders(ordersRes.data.data.items);
      } else if (ordersRes.data?.items) {
        setOrders(ordersRes.data.items);
      }
    } catch (err) {
      toast.error('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (productId) => {
    setSelectedItems(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === suggestions.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(suggestions.map(s => s.product_id));
    }
  };

  const handleCreateOrder = async () => {
    if (selectedItems.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    try {
      setCreateLoading(true);
      
      const items = selectedItems.map(productId => {
        const suggestion = suggestions.find(s => s.product_id === productId);
        return {
          product_id: productId,
          quantity: suggestion?.suggested_quantity || 0
        };
      });
      
      // Group by supplier if multiple
      const supplierIds = [...new Set(
        selectedItems
          .map(id => suggestions.find(s => s.product_id === id)?.supplier_id)
          .filter(Boolean)
      )];
      
      // Create order for first supplier (or all if no supplier)
      const response = await api.post('/purchase-orders', {
        supplier_id: supplierIds.length === 1 ? supplierIds[0] : null,
        items,
        notes: ''
      });
      
      if (response.data?.success) {
        toast.success('Ordem de compra criada com sucesso!');
        setSelectedItems([]);
        setShowCreateModal(false);
        loadData();
        setActiveTab('orders');
      }
    } catch (err) {
      toast.error('Erro ao criar ordem de compra');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleExportPDF = async (orderId) => {
    try {
      const response = await api.get(`/purchase-orders/${orderId}/export/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordem_compra_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleExportExcel = async (orderId) => {
    try {
      const response = await api.get(`/purchase-orders/${orderId}/export/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordem_compra_${orderId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      await api.put(`/purchase-orders/${orderId}/status?new_status=${newStatus}`);
      
      // Show specific notification based on status
      if (newStatus === 'received') {
        toast.success(
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-green-500" />
            <div>
              <div className="font-medium">Ordem Recebida!</div>
              <div className="text-sm text-muted-foreground">
                {order?.order_number} - Estoque atualizado automaticamente
              </div>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else if (newStatus === 'sent') {
        toast.success(`Ordem ${order?.order_number} enviada ao fornecedor!`);
      } else if (newStatus === 'cancelled') {
        toast.info(`Ordem ${order?.order_number} cancelada.`);
      } else {
        toast.success('Status atualizado!');
      }
      
      loadData();
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Toggle expand order details
  const toggleExpandOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Open edit modal
  const handleEditOrder = (order) => {
    if (order.status !== 'draft') {
      toast.error('Apenas ordens em rascunho podem ser editadas');
      return;
    }
    setEditingOrder(order);
    setEditItems(order.items?.map(item => ({
      ...item,
      quantity: item.quantity || 0
    })) || []);
    setShowEditModal(true);
  };

  // Update item quantity in edit mode
  const handleEditItemQuantity = (index, newQuantity) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: Math.max(0, newQuantity) };
      return updated;
    });
  };

  // Remove item from order
  const handleRemoveEditItem = (index) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  // Save edited order
  const handleSaveEdit = async () => {
    if (editItems.length === 0) {
      toast.error('A ordem deve ter pelo menos um item');
      return;
    }
    
    try {
      setEditLoading(true);
      
      // Calculate new totals
      const items = editItems.filter(item => item.quantity > 0);
      const totalItems = items.length;
      const totalQuantity = items.reduce((acc, item) => acc + item.quantity, 0);
      const totalCost = items.reduce((acc, item) => acc + (item.quantity * (item.unit_cost || 0)), 0);
      
      await api.put(`/purchase-orders/${editingOrder.id}`, {
        items: items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
          total_cost: item.quantity * (item.unit_cost || 0)
        })),
        total_items: totalItems,
        total_quantity: totalQuantity,
        total_cost: totalCost
      });
      
      toast.success('Ordem atualizada com sucesso!');
      setShowEditModal(false);
      setEditingOrder(null);
      loadData();
    } catch (err) {
      toast.error('Erro ao atualizar ordem');
    } finally {
      setEditLoading(false);
    }
  };

  const selectedSummary = useMemo(() => {
    const selected = suggestions.filter(s => selectedItems.includes(s.product_id));
    return {
      count: selected.length,
      totalQuantity: selected.reduce((acc, s) => acc + s.suggested_quantity, 0),
      totalCost: selected.reduce((acc, s) => acc + (s.suggested_quantity * (s.unit_cost || 0)), 0)
    };
  }, [selectedItems, suggestions]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">Carregando ordens de compra...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" />
            Ordens de Compra
          </h1>
          <p className="text-muted-foreground">
            Gere ordens de compra baseadas nos níveis de estoque
          </p>
        </div>
        <Button onClick={() => loadData()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Help Guide */}
      <HelpGuide
        title="Como funciona?"
        description="O sistema sugere compras baseado nos níveis de estoque configurados em cada produto."
        collapsible={true}
        defaultOpen={false}
        className="mb-6"
        sections={[
          {
            title: "Sugestões de Compra",
            content: "O sistema analisa todos os produtos e identifica aqueles que estão abaixo do estoque mínimo. A quantidade sugerida é a diferença entre o estoque máximo e o estoque atual.",
          }
        ]}
        tips={[
          "Configure o estoque mínimo e máximo em cada produto",
          "Produtos sem fornecedor aparecerão agrupados separadamente",
          "Você pode exportar as ordens em PDF ou Excel para enviar aos fornecedores"
        ]}
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'suggestions' ? 'default' : 'outline'}
          onClick={() => setActiveTab('suggestions')}
        >
          <Package className="h-4 w-4 mr-2" />
          Sugestões ({summary.total_products})
        </Button>
        <Button
          variant={activeTab === 'orders' ? 'default' : 'outline'}
          onClick={() => setActiveTab('orders')}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Ordens ({orders.length})
        </Button>
      </div>

      {activeTab === 'suggestions' && (
        <>
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="only-low"
                    checked={onlyLowStock}
                    onCheckedChange={setOnlyLowStock}
                  />
                  <Label htmlFor="only-low" className="flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Apenas estoque baixo
                  </Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label>Fornecedor:</Label>
                  <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1" />

                {selectedItems.length > 0 && (
                  <Button onClick={() => setShowCreateModal(true)} data-testid="create-order-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Ordem ({selectedItems.length})
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          {summary.total_products > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{summary.total_products}</div>
                  <div className="text-sm text-muted-foreground">Produtos para reposição</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{summary.suppliers_count}</div>
                  <div className="text-sm text-muted-foreground">Fornecedores</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-primary">{formatCurrency(summary.total_estimated_cost)}</div>
                  <div className="text-sm text-muted-foreground">Custo estimado total</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Products by Supplier */}
          {bySupplier.length === 0 ? (
            <HelpAlert variant="info" title="Nenhum produto para reposição">
              Todos os produtos estão com estoque adequado. Ajuste os filtros ou configure 
              os níveis de estoque nos produtos.
            </HelpAlert>
          ) : (
            <div className="space-y-4">
              {/* Select All */}
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Checkbox
                  checked={selectedItems.length === suggestions.length && suggestions.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <Label>Selecionar todos ({suggestions.length})</Label>
                
                {selectedItems.length > 0 && (
                  <Badge variant="secondary" className="ml-4">
                    {selectedSummary.count} selecionados • 
                    {selectedSummary.totalQuantity} unidades • 
                    {formatCurrency(selectedSummary.totalCost)}
                  </Badge>
                )}
              </div>

              {bySupplier.map((group) => (
                <Collapsible key={group.supplier_id || 'no_supplier'} defaultOpen>
                  <Card>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <CardTitle className="text-lg">{group.supplier_name}</CardTitle>
                              <CardDescription>
                                {group.total_items} produtos • {formatCurrency(group.estimated_cost)}
                              </CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        <div className="divide-y">
                          {group.items.map((item) => (
                            <div 
                              key={item.product_id} 
                              className="flex items-center gap-4 py-3"
                            >
                              <Checkbox
                                checked={selectedItems.includes(item.product_id)}
                                onCheckedChange={() => handleSelectItem(item.product_id)}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{item.product_name}</div>
                                <div className="text-sm text-muted-foreground">
                                  {item.sku && `SKU: ${item.sku} • `}
                                  {item.category_name}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground">Atual</div>
                                <Badge variant={item.current_stock <= item.min_stock ? 'destructive' : 'secondary'}>
                                  {item.current_stock}
                                </Badge>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground">Mín/Máx</div>
                                <div className="text-sm">{item.min_stock}/{item.max_stock}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground">Sugerido</div>
                                <Badge variant="outline" className="bg-primary/10">
                                  +{item.suggested_quantity}
                                </Badge>
                              </div>
                              <div className="text-right w-24">
                                <div className="text-sm text-muted-foreground">Custo Unit.</div>
                                <div className="text-sm">{formatCurrency(item.unit_cost || 0)}</div>
                              </div>
                              <div className="text-right w-24">
                                <div className="text-sm text-muted-foreground">Total</div>
                                <div className="font-medium">
                                  {formatCurrency((item.suggested_quantity || 0) * (item.unit_cost || 0))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'orders' && (
        <div className="space-y-6">
          {/* Status Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card 
              className={`cursor-pointer transition-all ${statusFilter === 'draft' ? 'ring-2 ring-gray-500' : 'hover:bg-muted/50'}`}
              onClick={() => setStatusFilter(statusFilter === 'draft' ? 'all' : 'draft')}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{orderStats.draft.count}</div>
                    <div className="text-sm text-muted-foreground">Em Aberto</div>
                  </div>
                  <Badge className="bg-gray-500">{formatCurrency(orderStats.draft.total)}</Badge>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${statusFilter === 'sent' ? 'ring-2 ring-blue-500' : 'hover:bg-muted/50'}`}
              onClick={() => setStatusFilter(statusFilter === 'sent' ? 'all' : 'sent')}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{orderStats.sent.count}</div>
                    <div className="text-sm text-muted-foreground">Enviadas</div>
                  </div>
                  <Badge className="bg-blue-500">{formatCurrency(orderStats.sent.total)}</Badge>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${statusFilter === 'received' ? 'ring-2 ring-green-500' : 'hover:bg-muted/50'}`}
              onClick={() => setStatusFilter(statusFilter === 'received' ? 'all' : 'received')}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-green-500">{orderStats.received.count}</div>
                    <div className="text-sm text-muted-foreground">Recebidas</div>
                  </div>
                  <Badge className="bg-green-500">{formatCurrency(orderStats.received.total)}</Badge>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${statusFilter === 'cancelled' ? 'ring-2 ring-red-500' : 'hover:bg-muted/50'}`}
              onClick={() => setStatusFilter(statusFilter === 'cancelled' ? 'all' : 'cancelled')}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-red-500">{orderStats.cancelled.count}</div>
                    <div className="text-sm text-muted-foreground">Canceladas</div>
                  </div>
                  <Badge className="bg-red-500">{formatCurrency(orderStats.cancelled.total)}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Filter Pills */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filtrar:</span>
            <Button
              size="sm"
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('all')}
            >
              Todas ({orders.length})
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'draft' ? 'default' : 'outline'}
              className={statusFilter === 'draft' ? 'bg-gray-600' : ''}
              onClick={() => setStatusFilter('draft')}
            >
              Em Aberto ({orderStats.draft.count})
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'sent' ? 'default' : 'outline'}
              className={statusFilter === 'sent' ? 'bg-blue-600' : ''}
              onClick={() => setStatusFilter('sent')}
            >
              Enviadas ({orderStats.sent.count})
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'received' ? 'default' : 'outline'}
              className={statusFilter === 'received' ? 'bg-green-600' : ''}
              onClick={() => setStatusFilter('received')}
            >
              Recebidas ({orderStats.received.count})
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'cancelled' ? 'default' : 'outline'}
              className={statusFilter === 'cancelled' ? 'bg-red-600' : ''}
              onClick={() => setStatusFilter('cancelled')}
            >
              Canceladas ({orderStats.cancelled.count})
            </Button>
          </div>

          {/* Orders List */}
          {filteredOrders.length === 0 ? (
            <HelpAlert variant="info" title="Nenhuma ordem de compra">
              {statusFilter === 'all' 
                ? 'Você ainda não criou nenhuma ordem de compra. Vá para a aba "Sugestões" para selecionar produtos e criar uma ordem.'
                : `Nenhuma ordem com status "${STATUS_LABELS[statusFilter]}".`
              }
            </HelpAlert>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <Collapsible 
                  key={order.id} 
                  open={expandedOrders[order.id]}
                  onOpenChange={() => toggleExpandOrder(order.id)}
                >
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CollapsibleTrigger className="flex items-center gap-3 hover:opacity-80 cursor-pointer">
                          {expandedOrders[order.id] ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div className="text-left">
                            <CardTitle className="flex items-center gap-2">
                              {order.order_number}
                              <Badge className={STATUS_COLORS[order.status]}>
                                {STATUS_LABELS[order.status]}
                              </Badge>
                            </CardTitle>
                            <CardDescription>
                              {order.supplier_name || 'Sem fornecedor'} • 
                              {order.total_items} itens • 
                              {formatCurrency(order.total_cost || 0)} • 
                              {new Date(order.created_at).toLocaleDateString('pt-BR')}
                            </CardDescription>
                          </div>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-2">
                          {/* Edit button - only for draft */}
                          {order.status === 'draft' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditOrder(order)}
                              title="Editar ordem"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                          )}
                          
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleExportPDF(order.id)}
                          >
                            <FileDown className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleExportExcel(order.id)}
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-1" />
                            Excel
                          </Button>
                          
                          {order.status === 'draft' && (
                            <Button 
                              size="sm"
                              onClick={() => handleUpdateStatus(order.id, 'sent')}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Enviar
                            </Button>
                          )}
                          
                          {order.status === 'sent' && (
                            <Button 
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleUpdateStatus(order.id, 'received')}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Recebido
                            </Button>
                          )}
                          
                          {(order.status === 'draft' || order.status === 'sent') && (
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleUpdateStatus(order.id, 'cancelled')}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {/* Expandable Details */}
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            Itens da Ordem ({order.items?.length || 0})
                          </h4>
                          <div className="bg-muted/50 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-muted">
                                <tr>
                                  <th className="text-left p-3">Produto</th>
                                  <th className="text-left p-3">SKU</th>
                                  <th className="text-right p-3">Qtd</th>
                                  <th className="text-right p-3">Custo Unit.</th>
                                  <th className="text-right p-3">Total</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-muted">
                                {order.items?.map((item, idx) => (
                                  <tr key={idx}>
                                    <td className="p-3">{item.product_name || '-'}</td>
                                    <td className="p-3 text-muted-foreground">{item.sku || '-'}</td>
                                    <td className="p-3 text-right font-medium">{item.quantity}</td>
                                    <td className="p-3 text-right">{formatCurrency(item.unit_cost || 0)}</td>
                                    <td className="p-3 text-right font-medium">{formatCurrency(item.total_cost || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-muted font-medium">
                                <tr>
                                  <td colSpan="2" className="p-3">Total</td>
                                  <td className="p-3 text-right">{order.total_quantity || order.items?.reduce((a, i) => a + (i.quantity || 0), 0)}</td>
                                  <td className="p-3"></td>
                                  <td className="p-3 text-right text-primary">{formatCurrency(order.total_cost || 0)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          
                          {order.notes && (
                            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                              <span className="text-sm text-muted-foreground">Observações: </span>
                              <span className="text-sm">{order.notes}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      <Modal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        title="Criar Ordem de Compra"
        description={`${selectedSummary.count} produtos selecionados`}
        confirmText="Criar Ordem"
        loading={createLoading}
        onConfirm={handleCreateOrder}
      >
        <div className="space-y-4">
          <HelpAlert variant="info">
            Uma ordem de compra será criada com os produtos selecionados.
            Você poderá exportar em PDF ou Excel para enviar ao fornecedor.
          </HelpAlert>
          
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Produtos</div>
              <div className="text-xl font-bold">{selectedSummary.count}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Quantidade Total</div>
              <div className="text-xl font-bold">{selectedSummary.totalQuantity}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Custo Estimado</div>
              <div className="text-xl font-bold text-primary">{formatCurrency(selectedSummary.totalCost)}</div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Order Modal */}
      <Modal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        title={`Editar Ordem ${editingOrder?.order_number || ''}`}
        description="Ajuste as quantidades ou remova itens da ordem"
        confirmText="Salvar Alterações"
        loading={editLoading}
        onConfirm={handleSaveEdit}
      >
        <div className="space-y-4">
          <HelpAlert variant="tip">
            Você pode ajustar as quantidades ou remover itens.
            Apenas ordens em rascunho podem ser editadas.
          </HelpAlert>
          
          {editItems.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              Nenhum item na ordem
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto divide-y">
              {editItems.map((item, index) => (
                <div key={index} className="flex items-center gap-4 py-3">
                  <div className="flex-1">
                    <div className="font-medium">{item.product_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.sku && `SKU: ${item.sku} • `}
                      Custo: {formatCurrency(item.unit_cost || 0)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditItemQuantity(index, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </Button>
                    <input
                      type="number"
                      className="w-16 text-center border rounded px-2 py-1"
                      value={item.quantity}
                      onChange={(e) => handleEditItemQuantity(index, parseInt(e.target.value) || 0)}
                      min={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditItemQuantity(index, item.quantity + 1)}
                    >
                      +
                    </Button>
                  </div>
                  <div className="w-24 text-right font-medium">
                    {formatCurrency((item.quantity || 0) * (item.unit_cost || 0))}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500"
                    onClick={() => handleRemoveEditItem(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Edit Summary */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total:</span>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(editItems.reduce((acc, item) => acc + ((item.quantity || 0) * (item.unit_cost || 0)), 0))}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
